/**
 * /entrar — invite-redemption page (T5 spec §8).
 *
 * NOTE: the canonical, user-facing invite link in the waitlist email points
 * at the marketing site (`cuidari.com/entrar`). This Ariadne `/entrar` is the
 * app-host (`app.cuidari.com`) twin of that page; it must behave identically,
 * so it enforces the same 12-char minimum Cerberus requires.
 *
 * Arrived at via the invite link:
 *   https://cuidari.com/entrar?invite=<token>&entry=<entry_id>
 *
 * Flow:
 *   1. Read invite + entry from URL.
 *   2. GET /v1/waitlist/entries/{entry}/email?token=<invite> →
 *      retrieves the original email (token-gated; no PII in the URL).
 *   3. Render a signup form with the email pre-filled (read-only), a
 *      12-char-minimum password and the LGPD consent checkboxes.
 *   4. On submit, POST Cerberus signup with email + password.
 *   5. On signup success, POST /v1/waitlist/entries/{entry}/convert
 *      with the invite token + the new user_id + the captured consent.
 *      Then show a "check your email" notice (signup triggers Cerberus
 *      email verification).
 */
import {
  useEffect,
  useState,
  type FormEvent,
  type CSSProperties,
} from 'react'
import { cerberusAuthClient, CerberusError } from '@/lib/cerberus/client'
import { PRODUCT_HOME_URL } from '@/lib/host'
import {
  convertEntry,
  getEntryEmail,
  GrowthApiError,
} from '@/modules/growth/api'

const CTA_TEAL = '#0f766e'

/**
 * Cerberus rejects passwords shorter than 12 chars (HIGH-5). The browser
 * `minLength` mirrors that so a 8–11-char password never reaches the API and
 * fails with an English server error.
 */
const PASSWORD_MIN_LENGTH = 12

/**
 * Map a Cerberus signup failure to PT-BR copy (HIGH-5). Cerberus surfaces a
 * machine `code` (e.g. `weak_password`) on `CerberusError`; we translate the
 * known ones and fall back to a friendly generic message for the rest rather
 * than leaking the raw English server string at the user.
 */
function signupErrorMessage(err: unknown): string {
  if (err instanceof CerberusError) {
    switch (err.code) {
      case 'weak_password':
      case 'password_too_short':
        return `Senha muito fraca — use ao menos ${PASSWORD_MIN_LENGTH} caracteres, com letras e números.`
      case 'email_already_exists':
      case 'user_already_exists':
        return 'Esse e-mail já tem uma conta. Faça login.'
      case 'invalid_invite':
      case 'invite_expired':
        return 'Convite inválido ou expirado. Entre na lista de espera novamente.'
      default:
        if (err.status === 429) {
          return 'Muitas tentativas. Aguarde alguns instantes e tente de novo.'
        }
        if (err.status >= 500) {
          return 'Nossos servidores tropeçaram. Tente de novo em instantes.'
        }
        return 'Não foi possível criar sua conta. Verifique os dados e tente de novo.'
    }
  }
  if (err instanceof Error) {
    return err.message
  }
  return 'Falha ao criar conta.'
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  borderRadius: 24,
  background: 'white',
  padding: 28,
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
}

const input: CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 14,
  border: '1.5px solid var(--color-border)',
  fontSize: 15,
  color: 'var(--color-fg)',
  background: 'white',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
}

const primaryBtn: CSSProperties = {
  width: '100%',
  padding: '16px',
  borderRadius: 99,
  background: CTA_TEAL,
  color: 'white',
  fontSize: 16,
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

interface UrlParams {
  invite: string | null
  entry: string | null
}

function readParams(): UrlParams {
  if (typeof window === 'undefined') return { invite: null, entry: null }
  const sp = new URLSearchParams(window.location.search)
  return { invite: sp.get('invite'), entry: sp.get('entry') }
}

// `already_account` is a distinct terminal state from `invalid`: the invite
// was real but has already been burned by a finished signup (LOW-6). We tell
// the user to log in instead of showing the generic "convite inválido".
type Phase =
  | 'loading'
  | 'invalid'
  | 'already_account'
  | 'ready'
  | 'signing_up'
  | 'verified'

export function EntrarPage() {
  const [phase, setPhase] = useState<Phase>('loading')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  // LGPD consent (MED-6). `terms` is mandatory to enable submit; `marketing`
  // is an opt-in that gates the marketing drip.
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [acceptMarketing, setAcceptMarketing] = useState(false)
  const [params] = useState<UrlParams>(() => readParams())

  useEffect(() => {
    const { invite, entry } = params
    if (!invite || !entry) {
      setError('Link de convite inválido.')
      setPhase('invalid')
      return
    }
    const ctrl = new AbortController()
    getEntryEmail(entry, invite, ctrl.signal)
      .then((res) => {
        setEmail(res.email)
        setPhase('ready')
      })
      .catch((err: unknown) => {
        if (err instanceof GrowthApiError) {
          // LOW-6: a converted/burned invite means the account already
          // exists — route to login instead of the generic error.
          if (
            err.code === 'ALREADY_CONVERTED' ||
            err.code === 'ENTRY_ALREADY_CONVERTED' ||
            err.status === 409 ||
            err.status === 410
          ) {
            setPhase('already_account')
            return
          }
          if (err.code === 'TOKEN_EXPIRED') {
            setError('Este convite expirou — entre na lista de espera novamente.')
          } else if (err.code === 'INVALID_TOKEN' || err.status === 401) {
            setError('Convite inválido. Verifique o link no e-mail.')
          } else if (err.status === 404) {
            setError('Convite não encontrado.')
          } else {
            setError('Não conseguimos carregar seu convite — tente em instantes.')
          }
        } else if (err instanceof Error && err.name === 'AbortError') {
          return
        } else if (err instanceof Error && err.message.includes('runtime-config')) {
          setError('Aplicativo desatualizado. Recarregue a página (Ctrl+Shift+R).')
        } else {
          console.error('[entrar] unexpected error', err)
          setError('Falha de rede. Verifique sua conexão.')
        }
        setPhase('invalid')
      })
    return () => ctrl.abort()
  }, [params])

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    if (!params.invite || !params.entry) return
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`)
      return
    }
    if (!acceptTerms) {
      setError('Você precisa aceitar os termos de uso para criar a conta.')
      return
    }
    setPhase('signing_up')
    setError(null)
    try {
      // `invite_token` signals waitlist-redemption to Cerberus so the
      // response includes `user_id`; `return_to` routes the verify
      // email back at the product app surface (app.cuidari.com/verify) instead
      // of the canonical IdP.
      //
      // PRODUCT_HOME_URL is the canonical product app host
      // prod) — using window.location.origin can leak the legacy
      // ariadne.francisquini.ai alias into the verify email when the
      // user happens to land on the alias CloudFront.
      const verifyReturnTo = `${new URL(PRODUCT_HOME_URL).origin}/verify`
      const signup = await cerberusAuthClient.signup({
        email,
        password,
        invite_token: params.invite,
        return_to: verifyReturnTo,
      })
      if (!signup.user_id) {
        // Cerberus must return user_id when invite_token is set; if
        // we got here without one, conversion is impossible — surface
        // it instead of silently leaving the entry stuck in `invited`.
        setError(
          'Cadastro criado, mas não conseguimos liberar seu acesso. Recarregue e tente novamente.',
        )
        setPhase('ready')
        return
      }
      await convertEntry(params.entry, params.invite, signup.user_id, {
        marketing: acceptMarketing,
        terms: acceptTerms,
        consented_at: new Date().toISOString(),
      })
      setPhase('verified')
    } catch (err) {
      // If conversion fails because the entry is already converted, the
      // account exists — send the user to login rather than re-stranding
      // them on the signup form (LOW-6).
      if (
        err instanceof GrowthApiError &&
        (err.status === 409 ||
          err.status === 410 ||
          err.code === 'ALREADY_CONVERTED')
      ) {
        setPhase('already_account')
        return
      }
      setError(signupErrorMessage(err))
      setPhase('ready')
    }
  }

  return (
    <main
      data-testid="entrar-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background:
          'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, var(--color-bg)) 0%, color-mix(in srgb, var(--color-accent) 8%, var(--color-bg)) 100%)',
      }}
    >
      <div style={card}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: 'var(--color-fg)',
            marginBottom: 12,
          }}
        >
          Sua vaga no Cuidari está pronta
        </h1>

        {phase === 'loading' && (
          <p
            data-testid="entrar-loading"
            style={{ fontSize: 14, color: 'var(--color-muted)' }}
          >
            Carregando convite…
          </p>
        )}

        {phase === 'invalid' && (
          <div data-testid="entrar-invalid" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p role="alert" style={{ fontSize: 14, color: 'var(--color-danger)' }}>
              {error}
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Voltar
            </a>
          </div>
        )}

        {phase === 'already_account' && (
          <div
            data-testid="entrar-already-account"
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <p style={{ fontSize: 14, color: 'var(--color-fg)', lineHeight: 1.5 }}>
              Você já criou sua conta — faça login para continuar.
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Ir para login
            </a>
          </div>
        )}

        {(phase === 'ready' || phase === 'signing_up') && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 14, color: 'var(--color-muted)' }}>
              Crie uma senha para finalizar seu cadastro.
            </p>
            {error && (
              <p
                role="alert"
                data-testid="entrar-error"
                style={{
                  fontSize: 13,
                  color: 'var(--color-danger)',
                  background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)',
                  padding: '10px 14px',
                  borderRadius: 12,
                }}
              >
                {error}
              </p>
            )}
            <input
              type="email"
              data-testid="entrar-email"
              value={email}
              readOnly
              style={{ ...input, background: 'var(--color-bg)', color: 'var(--color-muted)' }}
            />
            <input
              type="password"
              data-testid="entrar-password"
              placeholder={`Crie uma senha (mín. ${PASSWORD_MIN_LENGTH} caracteres)`}
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={input}
            />
            <p
              data-testid="entrar-password-hint"
              style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: -4 }}
            >
              Pelo menos {PASSWORD_MIN_LENGTH} caracteres, com letras e números.
            </p>

            {/* LGPD consent (MED-6). `terms` mandatory; `marketing` opt-in. */}
            <label
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--color-fg)', lineHeight: 1.4 }}
            >
              <input
                type="checkbox"
                data-testid="entrar-consent-terms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                Li e aceito os{' '}
                <a href="https://cuidari.com/termos" target="_blank" rel="noreferrer" style={{ color: CTA_TEAL, fontWeight: 600 }}>
                  Termos de Uso
                </a>{' '}
                e a{' '}
                <a href="https://cuidari.com/privacidade" target="_blank" rel="noreferrer" style={{ color: CTA_TEAL, fontWeight: 600 }}>
                  Política de Privacidade
                </a>
                .
              </span>
            </label>
            <label
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--color-fg)', lineHeight: 1.4 }}
            >
              <input
                type="checkbox"
                data-testid="entrar-consent-marketing"
                checked={acceptMarketing}
                onChange={(e) => setAcceptMarketing(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                Quero receber e-mails do Cuidari com dicas e novidades (opcional —
                você pode cancelar quando quiser).
              </span>
            </label>

            <button
              type="submit"
              data-testid="entrar-submit"
              disabled={phase === 'signing_up' || !acceptTerms}
              style={{
                ...primaryBtn,
                ...(phase === 'signing_up' || !acceptTerms
                  ? { opacity: 0.6, cursor: 'not-allowed' }
                  : {}),
              }}
            >
              {phase === 'signing_up' ? 'Criando conta…' : 'Criar minha conta'}
            </button>
          </form>
        )}

        {phase === 'verified' && (
          <div data-testid="entrar-verified" style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center' }}>
            <span style={{ fontSize: 48 }}>📬</span>
            <p style={{ fontSize: 16, fontWeight: 700 }}>Quase lá!</p>
            <p style={{ fontSize: 14, color: 'var(--color-muted)', lineHeight: 1.5 }}>
              Enviamos um link de confirmação para <strong>{email}</strong>. Clique nele
              para finalizar e fazer login.
            </p>
            <a href="/login" style={{ color: CTA_TEAL, fontWeight: 600 }}>
              Ir para login
            </a>
          </div>
        )}
      </div>
    </main>
  )
}
